<script setup lang="ts">
import { STEPS } from '../content/site-content';

/**
 * «Come funziona»: gli otto passi, dalla creazione dell'asta al file delle rose.
 *
 * Ogni passo porta l'etichetta di **chi** lo fa, perché la domanda vera di chi
 * legge non è «quali sono i passaggi» ma «quanto lavoro tocca a me». La riga
 * verticale che li unisce è disegnata con un bordo sull'elenco, non con un
 * pseudo-elemento per passo: così finisce sull'ultimo pallino e non oltre.
 */
</script>

<template>
  <section id="come-funziona" class="section section--sunken">
    <div class="page">
      <div class="section__head">
        <span class="eyebrow">Come funziona</span>
        <h2 class="section__title">Dalla creazione dell’asta alle rose scaricate</h2>
        <p class="section__lead">
          Un’asta si prepara in mezz’ora e si conduce senza toccare un foglio di calcolo.
        </p>
      </div>

      <ol class="steps">
        <li v-for="step in STEPS" :key="step.id" class="step">
          <span class="step__number">{{ step.number }}</span>
          <div class="step__body">
            <span class="step__actor" :class="`step__actor--${step.actor.toLowerCase()}`">
              {{ step.actor }}
            </span>
            <h3 class="step__title">{{ step.title }}</h3>
            <p class="step__text">{{ step.body }}</p>
          </div>
        </li>
      </ol>
    </div>
  </section>
</template>

<style scoped>
.steps {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: none;
}

.step {
  position: relative;
  display: grid;
  grid-template-columns: 2rem 1fr;
  gap: 0.9rem;
  padding-bottom: 1.75rem;
}

/* Il filo che unisce i pallini: non sull'ultimo, altrimenti scende nel vuoto. */
.step:not(:last-child)::before {
  content: '';
  position: absolute;
  top: 2.25rem;
  bottom: 0.25rem;
  left: 0.97rem;
  width: 2px;
  background: var(--color-border-strong);
}

.step:last-child {
  padding-bottom: 0;
}

.step__number {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius-pill);
  background: var(--color-primary);
  color: var(--color-primary-contrast);
  font-size: 0.875rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

/* La sezione sta su `--color-sunken`: le pillole tenute sulla superficie chiara
   si staccano dal fondo, mentre i loro `--color-*-soft` ci sparivano dentro. */
.step__actor {
  display: inline-block;
  margin-bottom: 0.35rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.55rem;
  background: var(--color-surface);
  color: var(--color-muted);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.step__actor--amministratore {
  border-color: color-mix(in srgb, var(--color-primary) 35%, transparent);
  color: var(--color-primary);
}

.step__actor--fantallenatore {
  border-color: color-mix(in srgb, var(--color-accent-text) 40%, transparent);
  color: var(--color-accent-text);
}

.step__title {
  font-size: 1.0625rem;
}

.step__text {
  margin-top: 0.35rem;
  color: var(--color-muted);
  font-size: 0.9375rem;
  max-width: 44rem;
}

@media (min-width: 48rem) {
  .step {
    grid-template-columns: 2.5rem 1fr;
    gap: 1.25rem;
  }

  .step:not(:last-child)::before {
    left: 1.22rem;
    top: 2.75rem;
  }

  .step__number {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1rem;
  }
}
</style>
